(() => {
  if (window.__elagiliProductCartControlLoaded) return;
  window.__elagiliProductCartControlLoaded = true;

  let cartQuantities = new Map();
  let cartLineKeys = new Map();
  const busy = new Set();

  const rebuild = (cart) => {
    cartQuantities = new Map();
    cartLineKeys = new Map();
    for (const item of cart?.items || []) {
      const id = String(item.variant_id);
      cartQuantities.set(id, (cartQuantities.get(id) || 0) + Number(item.quantity || 0));
      if (!cartLineKeys.has(id) && item.key) cartLineKeys.set(id, item.key);
    }
  };

  const fetchCart = async () => {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const cart = await response.json();
    rebuild(cart);
    return cart;
  };

  const renderControl = (control) => {
    const variantId = String(control.dataset.variantId || '');
    if (!variantId) return;

    const available = control.dataset.available === 'true';
    const qty = cartQuantities.get(variantId) || 0;
    const isBusy = busy.has(variantId);

    if (!available) {
      control.innerHTML = '<button type="button" class="elagili-product-soldout" disabled>Sold out</button>';
      return;
    }

    if (qty > 0) {
      control.innerHTML = `
        <div class="elagili-product-qty">
          <button type="button" class="elagili-product-minus" aria-label="Decrease quantity" ${isBusy ? 'disabled' : ''}>−</button>
          <span class="elagili-product-count">${qty}</span>
          <button type="button" class="elagili-product-plus" aria-label="Increase quantity" ${isBusy ? 'disabled' : ''}>+</button>
        </div>`;
    } else {
      control.innerHTML = `<button type="button" class="elagili-product-add" ${isBusy ? 'disabled' : ''}>Add to cart</button>`;
    }
  };

  const applyCardVariantOptions = (root = document) => {
    root.querySelectorAll?.('product-card[data-product-variants-size]').forEach((card) => {
      if (Number(card.dataset.productVariantsSize || 0) <= 1) return;

      const row = card.querySelector('.elagili-card-buy-row');
      const control = row?.querySelector('.elagili-card-control');
      const link = card.querySelector('.product-card__link[href]');
      if (!row || !control || !link) return;

      row.removeAttribute('data-variant-id');

      if (!control.querySelector('.elagili-card-options')) {
        control.innerHTML = `<a class="elagili-card-options" href="${link.href}">Options</a>`;
      }
    });
  };

  const ensureCardOptionStyle = () => {
    if (document.getElementById('elagili-card-options-style')) return;
    const style = document.createElement('style');
    style.id = 'elagili-card-options-style';
    style.textContent = `
      .elagili-card-options{
        display:inline-flex;align-items:center;justify-content:center;
        min-height:36px;padding:7px 14px;border:0;border-radius:999px;
        background:#354557;color:#fff;text-decoration:none;font:inherit;
        font-weight:600;line-height:1;white-space:nowrap;cursor:pointer;
        position:relative;z-index:30
      }
      .elagili-card-options:hover{background:#263441;color:#fff}
      @media(max-width:749px){
        .elagili-card-options{min-height:34px;padding:6px 11px;font-size:13px}
      }
    `;
    document.head.appendChild(style);
  };

  const renderAll = () => {
    document.querySelectorAll('[data-elagili-product-cart-control]').forEach(renderControl);
    applyCardVariantOptions(document);
  };

  const emitUpdate = (cart, variantId, quantity) => {
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        sourceId: 'elagili-product-cart-control',
        data: { source: 'elagili-product-cart-control', variantId, itemCount: quantity },
        resource: cart,
      },
    }));
  };

  const add = async (variantId) => {
    if (busy.has(variantId)) return;
    busy.add(variantId);
    renderAll();
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const cart = await fetchCart();
      emitUpdate(cart, variantId, cartQuantities.get(variantId) || 1);
    } catch (error) {
      console.error('Elagili product cart: add failed', error);
      try { await fetchCart(); } catch (_) {}
    } finally {
      busy.delete(variantId);
      renderAll();
    }
  };

  const change = async (variantId, quantity) => {
    if (busy.has(variantId)) return;
    busy.add(variantId);
    renderAll();
    try {
      if (!cartLineKeys.has(variantId)) await fetchCart();
      const key = cartLineKeys.get(variantId);
      if (!key) throw new Error('Cart line not found');

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: Math.max(0, Number(quantity) || 0) }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const cart = await response.json();
      rebuild(cart);
      emitUpdate(cart, variantId, cartQuantities.get(variantId) || 0);
    } catch (error) {
      console.error('Elagili product cart: quantity update failed', error);
      try { await fetchCart(); } catch (_) {}
    } finally {
      busy.delete(variantId);
      renderAll();
    }
  };

  document.addEventListener('click', (event) => {
    const addButton = event.target.closest?.('.elagili-product-add');
    const plusButton = event.target.closest?.('.elagili-product-plus');
    const minusButton = event.target.closest?.('.elagili-product-minus');
    if (!addButton && !plusButton && !minusButton) return;

    const control = event.target.closest('[data-elagili-product-cart-control]');
    const variantId = String(control?.dataset.variantId || '');
    if (!variantId) return;

    event.preventDefault();
    event.stopPropagation();

    const current = cartQuantities.get(variantId) || 0;
    if (addButton) add(variantId);
    else if (plusButton) change(variantId, current + 1);
    else if (minusButton) change(variantId, Math.max(0, current - 1));
  }, true);

  document.addEventListener('cart:update', (event) => {
    if (event.detail?.data?.source === 'elagili-product-cart-control') return;
    fetchCart().then(renderAll).catch(() => {});
  });

  document.addEventListener('variant:update', (event) => {
    const variant = event.detail?.resource;
    const variantId = String(variant?.id || '');
    if (!variantId) return;

    const control = document.querySelector('main [data-elagili-product-cart-control]');
    if (!control) return;

    control.dataset.variantId = variantId;
    control.dataset.available = variant.available ? 'true' : 'false';

    fetchCart().then(() => renderControl(control)).catch(() => renderControl(control));
  });

  document.addEventListener('shopify:section:load', (event) => {
    applyCardVariantOptions(event.target || document);
  });

  document.addEventListener('filter:update', () => {
    setTimeout(() => applyCardVariantOptions(document), 100);
  });

  window.addEventListener('pageshow', () => {
    ensureCardOptionStyle();
    applyCardVariantOptions(document);
    fetchCart().then(renderAll).catch(() => {});
  });

  const start = () => {
    ensureCardOptionStyle();
    applyCardVariantOptions(document);
    fetchCart().then(renderAll).catch(() => {});
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();